/*
  Logo Carousel (SR-154)

  Subclass of Dawn's SliderComponent (assets/global.js) that turns its
  one-tile-at-a-time counter model into the paged model in the design
  (Figma 305:80): a row of page dots between two chevrons.

  - A "page" is the number of tiles that fit the row at the current width,
    computed with Math.round rather than SliderComponent's Math.floor. The
    item offset includes the flex gap but the row's trailing edge has no gap,
    so floor() under-counts an exactly-fitting row by one.
  - Arrows advance one page. Dots jump to a page. Dots are rebuilt on resize
    because the page count depends on the viewport.
  - In the theme editor, selecting a block scrolls its tile into view.
*/
if (!customElements.get('logo-carousel')) {
  class LogoCarousel extends SliderComponent {
    constructor() {
      super();

      if (!this.slider || !this.nextButton) return;

      this.dots.addEventListener('click', this.onDotClick.bind(this));

      if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
      }
    }

    get dots() {
      if (!this._dots) this._dots = this.querySelector('.logo-carousel__dots');
      return this._dots;
    }

    get pageOffset() {
      return this.slidesPerPage * this.sliderItemOffset;
    }

    initPages() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.max(
        1,
        Math.round((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset)
      );
      this.totalPages = Math.max(1, Math.ceil(this.sliderItemsToShow.length / this.slidesPerPage));

      this.renderDots();
      this.currentPage = undefined;
      this.update();
    }

    update() {
      if (!this.slider || !this.nextButton || !this.sliderItemOffset) return;

      const previousPage = this.currentPage;
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      const atEnd = maxScroll - this.slider.scrollLeft <= 1;

      this.currentPage = atEnd
        ? this.totalPages
        : Math.min(this.totalPages, Math.round(this.slider.scrollLeft / this.pageOffset) + 1);

      if (this.currentPage !== previousPage) {
        this.updateDots();
        this.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow[(this.currentPage - 1) * this.slidesPerPage],
            },
          })
        );
      }

      this.prevButton.toggleAttribute('disabled', this.slider.scrollLeft <= 0);
      this.nextButton.toggleAttribute('disabled', atEnd);
    }

    onButtonClick(event) {
      event.preventDefault();
      const direction = event.currentTarget.name === 'next' ? 1 : -1;
      this.setSlidePosition(this.slider.scrollLeft + direction * this.pageOffset);
    }

    onDotClick(event) {
      const dot = event.target.closest('.logo-carousel__dot');
      if (!dot) return;
      const page = Number(dot.dataset.page);
      if (!page) return;
      this.setSlidePosition((page - 1) * this.pageOffset);
    }

    renderDots() {
      if (!this.dots) return;

      const template = this.dots.dataset.dotLabel || 'Go to page [index] of [total]';
      const sliderId = this.dots.dataset.sliderId;

      this.dots.replaceChildren(
        ...Array.from({ length: this.totalPages }, (_, index) => {
          const page = index + 1;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'logo-carousel__dot';
          button.dataset.page = String(page);
          button.setAttribute(
            'aria-label',
            template.replace('[index]', String(page)).replace('[total]', String(this.totalPages))
          );
          if (sliderId) button.setAttribute('aria-controls', sliderId);

          const marker = document.createElement('span');
          marker.className = 'logo-carousel__dot-marker';
          button.appendChild(marker);
          return button;
        })
      );
    }

    updateDots() {
      if (!this.dots) return;
      this.dots.querySelectorAll('.logo-carousel__dot').forEach((dot) => {
        const isActive = Number(dot.dataset.page) === this.currentPage;
        dot.classList.toggle('logo-carousel__dot--active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    onBlockSelect(event) {
      const slide = event.target;
      if (!this.slider || !this.slider.contains(slide)) return;
      // .slider--everywhere is position: relative, so offsetLeft is relative to the list.
      this.slider.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
    }
  }

  customElements.define('logo-carousel', LogoCarousel);
}
